import type { EntitySnapshot, Vec3 } from "@models/data/entityData";
import type { MaterialDto } from "@models/data/materialDto";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { SceneResetResult } from "./IProjectModel";
import type { ProjectModel } from "./ProjectModel";

/**
 * `ProjectModelReset` — partial of {@link ProjectModel} owning the bulk
 * scene-reset flow. Port of the reset region of
 * `Models/ProjectModel/ProjectModel_Utils.cs` (`ResetActiveSceneAsync`,
 * `ResetSceneExplosionsAsync`, `ResetSceneLibraryMaterialsAsync`,
 * `HardResetSceneCoreAsync`).
 *
 * Unity order: grab modifiers → collision annotations → explosions →
 * (optional) library materials → hard transform reset. The web port covers
 * explosions + materials + transforms; grab modifiers and collision
 * annotations don't exist on web yet.
 *
 * Deviations (documented per web port rules):
 * - No modifier offset (`GetModifierOffset`): the web renderer derives
 *   explosion offsets from the persisted `ExplosionFactor` at draw time, so
 *   snapshot transforms never include them — and explosions are reset to 1
 *   before transforms anyway.
 * - Persists per-snapshot via `updateEntitySnapshotAsync` (which also
 *   publishes `update_entitysnapshot`) instead of Unity's batched
 *   `UpdateEntitiesAsync`; same rows + events, more round-trips.
 * - The materials pass runs BEFORE the combined explosion+transform walk
 *   (Unity: explosions → materials → transforms). The three legs patch
 *   disjoint snapshot fields with no runtime interplay on web, so the
 *   ordering difference has no observable effect.
 * - Not undoable — Unity passes no `UndoableAction` flag here either.
 */
export class ProjectModelReset {
  async resetActiveSceneAsync(
    this: ProjectModel,
    resetLibraryMaterials = false,
  ): Promise<SceneResetResult> {
    const scene = this.activeScene;
    if (!scene) return { explosionsReset: 0, partsReset: 0 };
    const sceneId = scene.id;

    // Unity guards with a per-scene `m_sceneResetLock`; collapse concurrent
    // resets of the same scene into the in-flight run.
    const inFlight = resetLocks.get(this);
    if (inFlight?.has(sceneId)) {
      return { explosionsReset: 0, partsReset: 0 };
    }
    const locks = inFlight ?? new Set<string>();
    resetLocks.set(this, locks);
    locks.add(sceneId);

    try {
      if (resetLibraryMaterials) {
        await resetSceneLibraryMaterialsAsync(this, sceneId);
      }

      let explosionsReset = 0;
      let partsReset = 0;

      // Unexplode first (Unity `ResetSceneExplosionsAsync`), then hard-reset
      // placements (`HardResetSceneCoreAsync` — position + rotation only).
      // Scale is intentionally NOT reset here: Unity's scene hard reset leaves
      // scale untouched, and original-imported-scale restore
      // (`GetOriginalScale`, 2026-07 Eva merge) lives only in the per-entity
      // `PlacementModel.resetScaleAsync` path (which writes
      // `resetTransform.scale` = the library `PartData` scale). Do NOT add a
      // scale write here — it would diverge from Unity.
      for (const entity of this.getEntities(sceneId)) {
        for (const snapshot of walkSnapshots(entity.snapshots)) {
          if (this.activeScene?.id !== sceneId) {
            // Scene changed mid-reset (Unity re-checks under the lock) — stop.
            return { explosionsReset, partsReset };
          }
          if (snapshot.isRoot && snapshot.explosionFactor !== 1) {
            await this.updateEntitySnapshotAsync(sceneId, snapshot.id, {
              explosionFactor: 1,
            });
            explosionsReset++;
          }
          if (needsHardReset(snapshot)) {
            await this.updateEntitySnapshotAsync(sceneId, snapshot.id, {
              transform: {
                position: snapshot.resetTransform.position,
                rotation: snapshot.resetTransform.rotation,
              },
            });
            partsReset++;
          }
        }
      }
      return { explosionsReset, partsReset };
    } finally {
      locks.delete(sceneId);
    }
  }
}

/** Per-model per-scene reset locks (Unity `m_sceneResetLock`). */
const resetLocks = new WeakMap<object, Set<string>>();

/** Protected `ProjectModel` state the partial reaches (mixin-cast pattern,
 *  same as `ProjectModelUpload`). */
interface ResetInternals {
  readonly m_partMaterialIdsById: Map<string, ReadonlyArray<string>>;
  readonly m_materialsById: Map<string, MaterialDto>;
  getStorageContext(): {
    storage: IStorageModel;
    customerId: string;
    projectId: string;
  } | null;
}

/**
 * Port of `ResetSceneLibraryMaterialsAsync` (`ProjectModel_Utils.cs:346-479`):
 * restores each snapshot's `MaterialIDs` to the library part row's list.
 *
 * Unity semantics kept exactly:
 * - Only library-saved assets participate (`AssetVersion <= 0` skips the
 *   entity — 0 means "never saved to the asset library"). Web "Add Model"
 *   imports are doubly excluded (their part rows carry no `MaterialIDs`).
 * - Merged-empty snapshots are skipped (`HasRuntimeMaterials` stand-in,
 *   AI-1173-consistent).
 * - The changed compare is ORDER-SENSITIVE (`SequenceEqual`).
 * - A part referencing a missing library material row is skipped ENTIRELY
 *   (no write, no event) with Unity's warning copy.
 * - Referenced library rows are force-refreshed from cloud once per run
 *   (`RefreshMaterialAsync` equivalent: overwrite `m_materialsById`).
 * - One `update_entitysnapshot` (tc 14, Materials flag) per changed
 *   snapshot, via `updateEntitySnapshotAsync`.
 * - Removed (scene-copied) material rows are NOT deleted — Unity's delete
 *   is deliberately commented out (C3D-8736 instancing); orphan rows are
 *   expected. Do not add cleanup.
 */
async function resetSceneLibraryMaterialsAsync(
  model: ProjectModel,
  sceneId: string,
): Promise<number> {
  const state = model as unknown as ResetInternals;
  const ctx = state.getStorageContext();
  if (!ctx) return 0; // offline — no library rows to restore from

  const entities = model.getEntities(sceneId);
  if (entities.length === 0) return 0;

  const assetVersionByAssetId = new Map<string, number>();
  const refreshedMaterialIds = new Set<string>();
  const missingMaterialIds = new Set<string>();
  let changed = 0;

  for (const entity of entities) {
    const assetId = entity.assetId;
    if (!assetId) continue;

    let version = assetVersionByAssetId.get(assetId);
    if (version === undefined) {
      try {
        const assetDto = await ctx.storage.getAssetById(ctx.customerId, assetId);
        version =
          typeof assetDto?.AssetVersion === "number" && Number.isFinite(assetDto.AssetVersion)
            ? assetDto.AssetVersion
            : 0;
      } catch (err) {
        console.warn(`[ProjectModel] reset materials: getAssetById failed (${assetId}):`, err);
        version = 0;
      }
      assetVersionByAssetId.set(assetId, version);
    }
    if (version <= 0) continue;

    for (const snapshot of walkSnapshots(entity.snapshots)) {
      if (model.activeScene?.id !== sceneId) return changed; // scene switched mid-run
      const libIds = snapshot.partId
        ? state.m_partMaterialIdsById.get(snapshot.partId)
        : undefined;
      if (!libIds || libIds.length === 0) continue;
      if (snapshot.hasMergedChildren && snapshot.materialIds.length === 0) continue;

      const toFetch = libIds.filter(
        (id) => !refreshedMaterialIds.has(id) && !missingMaterialIds.has(id),
      );
      if (toFetch.length > 0) {
        try {
          const rows = await ctx.storage.getMaterialBatch(ctx.customerId, toFetch);
          for (const row of rows) {
            state.m_materialsById.set(row.ID, row);
            refreshedMaterialIds.add(row.ID);
          }
        } catch (err) {
          console.warn(
            `[ProjectModel] reset materials: getMaterialBatch failed (${toFetch.length} ids):`,
            err,
          );
        }
        for (const id of toFetch) {
          if (!refreshedMaterialIds.has(id)) missingMaterialIds.add(id);
        }
      }

      const missing = libIds.filter((id) => !state.m_materialsById.has(id));
      if (missing.length > 0) {
        for (const id of missing) {
          // Unity warning copy verbatim (ProjectModel_Utils.cs).
          console.warn(
            `Reset scene could not restore material '${id}' for part '${snapshot.name}' because it was not found in asset '${assetId}'.`,
          );
        }
        continue; // whole part untouched (Unity parity)
      }

      const materialIdsChanged =
        snapshot.materialIds.length !== libIds.length
        || snapshot.materialIds.some((id, i) => id !== libIds[i]);
      if (!materialIdsChanged) continue;

      await model.updateEntitySnapshotAsync(sceneId, snapshot.id, {
        materialIds: [...libIds],
      });
      changed++;
    }
  }
  return changed;
}

function* walkSnapshots(
  snapshots: ReadonlyArray<EntitySnapshot>,
): Generator<EntitySnapshot> {
  for (const snapshot of snapshots) {
    yield snapshot;
    yield* walkSnapshots(snapshot.children);
  }
}

/**
 * Unity `HardResetScenePartRecursive` compares `localPosition` /
 * `localRotation` against the hard-reset pose (Unity's `Vector3 ==` is a
 * ~1e-5 approximate compare).
 */
function needsHardReset(snapshot: EntitySnapshot): boolean {
  return (
    !vec3Approximately(snapshot.transform.position, snapshot.resetTransform.position)
    || !vec3Approximately(snapshot.transform.rotation, snapshot.resetTransform.rotation)
  );
}

function vec3Approximately(a: Vec3, b: Vec3): boolean {
  const epsilon = 1e-5;
  return (
    Math.abs(a[0] - b[0]) <= epsilon
    && Math.abs(a[1] - b[1]) <= epsilon
    && Math.abs(a[2] - b[2]) <= epsilon
  );
}
